import { emptyNoteDraft, type NoteDraft, type NoteUpdateHandler } from "@/lib/note-ui";
import React, { useState, useRef, useEffect } from "react";
import { Lead, NotesEntry } from "@/components/lead-card";
import { LeadForm } from "@/components/lead-form";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader
} from "@/components/ui/sheet";
import { X, GripVertical, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

interface LeadDrawerProps {
  lead?: Lead;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (lead: Partial<Lead>) => void;
  isNew: boolean;
  onLeadUpdate?: NoteUpdateHandler;
}

export function LeadDrawer({ lead, isOpen, onClose, onSubmit, isNew, onLeadUpdate }: LeadDrawerProps) {
  const { currentUser } = useAuth();
  const [noteDrafts, setNoteDrafts] = useState<Record<string, NoteDraft>>({});
  const draftKey = `${currentUser?.id || ""}:${lead?.id || "new"}`;
  const noteDraft = noteDrafts[draftKey] || emptyNoteDraft;
  const onNoteDraftChange: React.Dispatch<React.SetStateAction<NoteDraft>> = update => {
    setNoteDrafts(current => ({ ...current, [draftKey]: typeof update === "function"
      ? update(current[draftKey] || emptyNoteDraft) : update }));
  };
  const [drawerWidth, setDrawerWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const formDataRef = useRef<Partial<Lead> | null>(null);

  const handleSubmit = (data: Partial<Lead>) => {
    onSubmit(data);
  };

  const handleFormDataChange = (data: Partial<Lead>) => {
    formDataRef.current = data;
  };

  const handleClose = () => {
    // Save form data before closing
    if (formDataRef.current) {
      // Remove undefined values and id field to prevent Firebase errors
      const cleanedData = Object.entries(formDataRef.current).reduce((acc, [key, value]) => {
        if (value !== undefined && key !== 'id') {
          acc[key] = value;
        }
        return acc;
      }, {} as Partial<Lead>);

      // Convert Date objects to ISO strings for Firestore
      if (cleanedData.nextContactDate) {
        if (cleanedData.nextContactDate instanceof Date) {
          cleanedData.nextContactDate = cleanedData.nextContactDate.toISOString();
        } else if (typeof cleanedData.nextContactDate === 'object' && !(cleanedData.nextContactDate instanceof Date)) {
          // Handle empty object {} or Firestore Timestamp
          const dateAny = cleanedData.nextContactDate as any;
          if (typeof dateAny.toDate === 'function') {
            cleanedData.nextContactDate = dateAny.toDate().toISOString();
          } else if (dateAny.__time__) {
            cleanedData.nextContactDate = dateAny.__time__;
          } else if (dateAny.seconds !== undefined) {
            cleanedData.nextContactDate = new Date(dateAny.seconds * 1000).toISOString();
          } else {
            // Empty object or unknown format - clear it
            cleanedData.nextContactDate = "";
          }
        }
      }

      // For new leads, check if at least firstName or lastName is filled
      if (isNew) {
        const hasRequiredData = cleanedData.firstName || cleanedData.lastName;
        if (hasRequiredData) {
          onSubmit(cleanedData);
        }
      } else if (lead?.id) {
        // Existing lead - always save
        // Bei Status "not-reached" automatisch Notiz erstellen
        if (cleanedData.status === "not-reached" && lead.status !== "not-reached") {
          const noteContent = "Telefonisch nicht erreicht";
          const now = new Date();

          const newNoteEntry: NotesEntry = {
            id: crypto.randomUUID(),
            content: noteContent,
            timestamp: now.toISOString(),
            createdBy: currentUser?.id || "System"
          };

          const currentNotes = lead.notesHistory || [];
          const updatedNotes = [newNoteEntry, ...currentNotes];

          // Notiz zu cleanedData hinzufügen, damit onSubmit die aktuellen Daten hat
          cleanedData.notesHistory = updatedNotes;
        }

        onSubmit(cleanedData);
      }
    }
    formDataRef.current = null;
    onClose();
  };

  const isPurchasedLead = lead && lead.marketplacePurchaseInfo;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 500 && newWidth <= 1200) {
        setDrawerWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        ref={drawerRef}
        className="flex flex-col p-0 gap-0 border-l overflow-hidden"
        side="right"
        style={{ width: `${drawerWidth}px`, maxWidth: "1200px", minWidth: "500px" }}
      >
        {/* Resize Handle */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 transition-colors z-50",
            isResizing && "bg-primary/40"
          )}
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-border rounded-full p-1">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <SheetHeader className="px-6 pt-4 pb-6 border-b sticky top-0 z-10 bg-background">
          <button
            onClick={handleClose}
            className="absolute right-6 top-4 rounded-sm opacity-70 hover:opacity-100 ring-offset-background transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Schließen</span>
          </button>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <LeadForm
            key={draftKey}
            lead={lead}
            noteDraft={noteDraft}
            onNoteDraftChange={onNoteDraftChange}
            isOpen={isOpen}
            onClose={handleClose}
            onSubmit={handleSubmit}
            onFormDataChange={handleFormDataChange}
            isNew={isNew}
            isDrawer={true}
            onLeadUpdate={onLeadUpdate}
          />

          {isPurchasedLead && !isNew && (
            <div className="px-6 pb-6">
              <div className={cn(
                "w-full p-3 rounded-lg border border-purple-400 bg-purple-50 flex items-center",
                "text-purple-800 shadow-sm"
              )}>
                <Tag className="h-4 w-4 mr-2 text-purple-600" />
                <div className="text-xs font-medium">
                  {lead.marketplacePurchaseInfo}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
